import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Stack,
  Box,
  Avatar,
  Button,
} from "@mui/material";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  AssignmentRecord,
  assignmentActivityReportByPortalsType,
  ProgressStatus,
} from "../../../../../services/api/types/reports/reports";
import { useCustomisation } from "../../../../../context/CustomisationContext";
import { useToast } from "../../../../../context/ToastContext";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";
import { VersionChip } from "./VersionChip";
import { PackageType } from "../../../../../services/api/types/assignments/assignment";

interface DetailedReportByPortalTableProps {
  data: assignmentActivityReportByPortalsType[];
}

const getInitials = (first: string, last: string) =>
  `${first?.[0] ?? ""}${last?.[0] ?? ""}`.toUpperCase();

const formatDateCell = (date: string | null | undefined) =>
  date ? dayjs(date).format("DD-MMM-YYYY") : "-";

const getLatestRecord = (
  records: AssignmentRecord[] | undefined,
): AssignmentRecord | null => {
  const list = records ?? [];
  if (list.length === 0) return null;
  return [...list].sort((a, b) => b.recordNo - a.recordNo)[0];
};

const getPlacements = (records: AssignmentRecord[] | undefined): string => {
  const list = records ?? [];
  const names = [
    ...new Set(list.map((record) => record.portalGroupName || "Homepage")),
  ];
  return names.length > 0 ? names.join(", ") : "-";
};

interface PortalUserSectionProps {
  user: any;
  borderColor: string;
  headerBg: string;
  homeCompletedColor: string;
  homePendingColor: string;
  homeOverdueColor: string;
  portalUid: string | undefined;
  showToast: (msg: string, type: any) => void;
  isLast: boolean;
}

const PortalUserSection: React.FC<PortalUserSectionProps> = ({
  user,
  borderColor,
  headerBg,
  homeCompletedColor,
  homePendingColor,
  homeOverdueColor,
  portalUid,
  showToast,
  isLast,
}) => {
  const tableScrollRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{
    startX: number;
    startScrollLeft: number;
    trackW: number;
    thumbW: number;
  } | null>(null);
  const [scrollbar, setScrollbar] = useState({
    show: false,
    thumbWidth: 0,
    thumbLeft: 0,
  });
  const SCROLL_ARROW_W = 44;

  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const refreshScrollbar = useCallback(() => {
    const el = tableScrollRef.current;
    if (!el || dragRef.current) return;
    const scrollable = el.scrollWidth > el.clientWidth + 1;
    if (!scrollable) {
      setScrollbar((s) => ({ ...s, show: false }));
      return;
    }
    const trackW = el.clientWidth - SCROLL_ARROW_W * 2;
    const thumbW = Math.max((el.clientWidth / el.scrollWidth) * trackW, 44);
    const maxScroll = el.scrollWidth - el.clientWidth;
    const thumbLeft =
      maxScroll > 0 ? (el.scrollLeft / maxScroll) * (trackW - thumbW) : 0;
    setScrollbar({ show: true, thumbWidth: thumbW, thumbLeft });
  }, [SCROLL_ARROW_W]);

  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", refreshScrollbar, { passive: true });
    const ro = new ResizeObserver(refreshScrollbar);
    ro.observe(el);
    refreshScrollbar();
    return () => {
      el.removeEventListener("scroll", refreshScrollbar);
      ro.disconnect();
    };
  }, [refreshScrollbar]);

  useEffect(() => {
    const move = (clientX: number) => {
      const d = dragRef.current;
      if (!d) return;
      const el = tableScrollRef.current;
      if (!el) return;
      const maxScroll = el.scrollWidth - el.clientWidth;
      const scale = maxScroll / Math.max(d.trackW - d.thumbW, 1);
      const newScrollLeft = Math.max(
        0,
        Math.min(maxScroll, d.startScrollLeft + (clientX - d.startX) * scale),
      );
      el.scrollLeft = newScrollLeft;
      if (thumbRef.current) {
        const newLeft =
          maxScroll > 0
            ? (newScrollLeft / maxScroll) * (d.trackW - d.thumbW)
            : 0;
        thumbRef.current.style.left = `${newLeft}px`;
      }
    };
    const end = () => {
      if (!dragRef.current) return;
      dragRef.current = null;
      refreshScrollbar();
    };
    const onMM = (e: MouseEvent) => move(e.clientX);
    const onTM = (e: TouchEvent) => {
      if (!dragRef.current) return;
      e.preventDefault();
      move(e.touches[0].clientX);
    };
    document.addEventListener("mousemove", onMM);
    document.addEventListener("mouseup", end);
    document.addEventListener("touchmove", onTM, { passive: false });
    document.addEventListener("touchend", end);
    return () => {
      document.removeEventListener("mousemove", onMM);
      document.removeEventListener("mouseup", end);
      document.removeEventListener("touchmove", onTM);
      document.removeEventListener("touchend", end);
    };
  }, [refreshScrollbar]);

  const onThumbDragStart = (clientX: number) => {
    const el = tableScrollRef.current;
    if (!el) return;
    dragRef.current = {
      startX: clientX,
      startScrollLeft: el.scrollLeft,
      trackW: el.clientWidth - SCROLL_ARROW_W * 2,
      thumbW: scrollbar.thumbWidth,
    };
  };

  const scrollByAmount = (amount: number) => {
    tableScrollRef.current?.scrollBy({ left: amount, behavior: "smooth" });
  };

  const toggleRow = (uid: string) => {
    if (!uid) return;
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  };

  const getStatusStyle = (progressStatus: ProgressStatus) => {
    if (!progressStatus)
      return {
        bg: `${homePendingColor}1A`,
        color: homePendingColor,
        dot: homePendingColor,
      };
    const status = progressStatus.toLowerCase();
    if (status.includes("overdue"))
      return {
        bg: `${homeOverdueColor}1A`,
        color: homeOverdueColor,
        dot: homeOverdueColor,
      };
    if (status.includes("completed"))
      return {
        bg: `${homeCompletedColor}1A`,
        color: homeCompletedColor,
        dot: homeCompletedColor,
      };
    return {
      bg: `${homePendingColor}1A`,
      color: homePendingColor,
      dot: homePendingColor,
    };
  };

  const StatusChip = ({ status }: { status: ProgressStatus }) => {
    const { bg, color, dot } = getStatusStyle(status);
    return (
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.75,
          px: 1.25,
          py: 0.4,
          borderRadius: "16px",
          backgroundColor: bg,
          border: `1px solid ${dot}33`,
          whiteSpace: "nowrap",
        }}
      >
        <Box
          sx={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            backgroundColor: dot,
            flexShrink: 0,
          }}
        />
        <Typography fontSize={12} fontWeight={500} color={color} lineHeight={1}>
          {status || "Pending"}
        </Typography>
      </Box>
    );
  };

  const attributeKeys = Object.keys(user.userAttributes || {}).slice(0, 4);

  return (
    <Box sx={{ borderBottom: isLast ? "none" : `1px solid ${borderColor}` }}>
      {/* User header — outside TableContainer so it stays fixed when scrolling horizontally */}
      <Box
        sx={{
          backgroundColor: headerBg,
          borderBottom: `1px solid ${borderColor}`,
          py: 1.5,
          px: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Avatar
            sx={{
              width: 36,
              height: 36,
              fontSize: 13,
              fontWeight: 600,
              bgcolor: "#E0E7FF",
              color: "#3730A3",
              flexShrink: 0,
            }}
          >
            {getInitials(user.firstName, user.lastName)}
          </Avatar>
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Typography fontWeight={600} color="#101828" fontSize={14}>
                {user.firstName} {user.lastName}
              </Typography>
              <Typography fontSize={13} color="#475467">
                {user.email}
              </Typography>
            </Box>
            {attributeKeys.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  mt: 0.4,
                  flexWrap: "wrap",
                }}
              >
                {attributeKeys.map((key: string, i: number) => (
                  <React.Fragment key={key}>
                    <Typography fontSize={12} color="#475467" fontWeight={600}>
                      {key.replace(/_/g, " ")}:{" "}
                      <Typography
                        component="span"
                        fontSize={12}
                        color="#475467"
                        fontWeight={600}
                      >
                        {user.userAttributes[key] || "-"}
                      </Typography>
                    </Typography>
                    {i < attributeKeys.length - 1 && (
                      <Typography fontSize={12} color="#475467">
                        •
                      </Typography>
                    )}
                  </React.Fragment>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Scrollable table */}
      <TableContainer
        ref={tableScrollRef}
        sx={{
          overflowX: "auto",
          WebkitOverflowScrolling: "touch",
          touchAction: "pan-x pan-y",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        <Table sx={{ minWidth: { xs: 950, md: "auto" } }}>
          <TableHead>
            <TableRow
              sx={{
                "& th": {
                  fontSize: 14,
                  color: "#475467",
                  fontWeight: 500,
                  borderBottom: `1px solid ${borderColor}`,
                  backgroundColor: headerBg,
                },
              }}
            >
              <TableCell
                sx={{
                  maxWidth: { md: 333 },
                  minWidth: { xs: 220, md: "auto" },
                  position: { xs: "sticky", md: "static" },
                  left: { xs: 0, md: "auto" },
                  zIndex: { xs: 2, md: "auto" },
                  backgroundColor: headerBg,
                }}
              >
                Assignments
              </TableCell>
              <TableCell sx={{ minWidth: { xs: 100, md: "auto" } }}>
                Version
              </TableCell>
              <TableCell sx={{ minWidth: { xs: 130, md: "auto" } }}>
                Placement
              </TableCell>
              <TableCell sx={{ minWidth: { xs: 130, md: "auto" } }}>
                Status
              </TableCell>
              <TableCell sx={{ minWidth: { xs: 180, md: "auto" } }}>
                Best score / Attempts
              </TableCell>
              <TableCell sx={{ minWidth: { xs: 110, md: "auto" } }}>
                Time spent
              </TableCell>
              <TableCell sx={{ minWidth: { xs: 120, md: "auto" } }}>
                Deadline
              </TableCell>
              <TableCell sx={{ minWidth: { xs: 110, md: "auto" } }}>
                Started
              </TableCell>
              <TableCell sx={{ minWidth: { xs: 110, md: "auto" } }}>
                Completed
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {user.assignments && user.assignments.length > 0 ? (
              user.assignments.map((assignment: any) => {
                const rowKey = `${user.userUid}_${assignment.userAssignmentUid || assignment.portalAssignmentUid}`;
                const isExpanded = expandedRows.has(rowKey);
                const latestRecord = getLatestRecord(assignment.records);
                const rowBg = "#ffffff";

                return (
                  <React.Fragment key={rowKey}>
                    <TableRow
                      sx={{
                        backgroundColor: rowBg,
                        "& td": {
                          fontSize: 13,
                          color: "#475467",
                          borderBottom: `1px solid ${borderColor}`,
                          py: 1.5,
                        },
                        "&:hover": { backgroundColor: "#F2F4F7" },
                      }}
                    >
                      <TableCell
                        sx={{
                          position: { xs: "sticky", md: "static" },
                          left: { xs: 0, md: "auto" },
                          zIndex: { xs: 1, md: "auto" },
                          backgroundColor: { xs: rowBg, md: "inherit" },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <Box
                            onClick={() => toggleRow(rowKey)}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              cursor: "pointer",
                              flexShrink: 0,
                            }}
                          >
                            {isExpanded ? (
                              <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />
                            ) : (
                              <KeyboardArrowRightIcon sx={{ fontSize: 18 }} />
                            )}
                          </Box>
                          <Box>
                            <Typography
                              color="#101828"
                              fontWeight={500}
                              fontSize={14}
                              sx={{
                                maxWidth: { md: 300 },
                                whiteSpace: "normal",
                                wordBreak: "break-word",
                              }}
                            >
                              {assignment.title}
                            </Typography>
                            <Typography
                              fontSize={14}
                              color="#475467"
                              fontWeight={400}
                            >
                              {assignment.type}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <VersionChip version={latestRecord?.version} isLatest />
                      </TableCell>
                      <TableCell>{getPlacements(assignment.records)}</TableCell>
                      <TableCell>
                        <StatusChip
                          status={
                            latestRecord?.progressStatus ??
                            assignment.progressStatus
                          }
                        />
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const attempts =
                            latestRecord?.attempts ?? assignment.attempts;
                          const score =
                            latestRecord?.bestScore ?? assignment.bestScore;
                          return assignment.packageType ===
                            PackageType.PdfUpload ||
                            assignment.packageType === PackageType.VideoUpload
                            ? "-"
                            : `${score != null ? score : "0"}% • ${attempts} ${attempts === 1 ? "Attempt" : "Attempts"}`;
                        })()}
                      </TableCell>
                      <TableCell>
                        {(latestRecord?.timeSpent ?? assignment.timeSpent)
                          ? `${latestRecord?.timeSpent ?? assignment.timeSpent} mins`
                          : "-"}
                      </TableCell>
                      <TableCell>
                        {formatDateCell(
                          latestRecord?.dueDate ?? assignment.dueDate,
                        )}
                      </TableCell>
                      <TableCell>
                        {formatDateCell(
                          latestRecord?.startDate ?? assignment.startDate,
                        )}
                      </TableCell>
                      <TableCell>
                        {formatDateCell(
                          latestRecord?.completionDate ??
                            assignment.completionDate,
                        )}
                      </TableCell>
                    </TableRow>

                    {isExpanded &&
                      [...(assignment.records ?? [])]
                        .sort(
                          (a: AssignmentRecord, b: AssignmentRecord) =>
                            a.recordNo - b.recordNo,
                        )
                        .map((record: AssignmentRecord) => (
                          <TableRow
                            key={record.recordUid}
                            sx={{
                              backgroundColor: "#FAFAFA",
                              "& td": {
                                fontSize: 13,
                                color: "#475467",
                                borderBottom: `1px solid ${borderColor}`,
                                py: 1.25,
                              },
                              "&:hover": { backgroundColor: "#F2F4F7" },
                            }}
                          >
                            <TableCell
                              sx={{
                                pl: 6,
                                position: { xs: "sticky", md: "static" },
                                left: { xs: 0, md: "auto" },
                                zIndex: { xs: 1, md: "auto" },
                                backgroundColor: {
                                  xs: "#FAFAFA",
                                  md: "inherit",
                                },
                              }}
                            >
                              <Button
                                variant="text"
                                size="small"
                                onClick={() => {
                                  if (
                                    assignment.packageType !==
                                      PackageType.PdfUpload &&
                                    assignment.packageType !==
                                      PackageType.VideoUpload &&
                                    !record.attempts
                                  ) {
                                    showToast(
                                      "No attempts available for this record.",
                                      "warning",
                                    );
                                    return;
                                  } else if (
                                    (assignment.packageType ===
                                      PackageType.PdfUpload &&
                                      record.ackResponse === null) ||
                                    (assignment.packageType ===
                                      PackageType.VideoUpload &&
                                      record.ackResponse === null)
                                  ) {
                                    showToast(
                                      "No acknowledgement available for this record.",
                                      "warning",
                                    );
                                    return;
                                  }
                                  const params = new URLSearchParams({
                                    userAssignmentUid: record.userAssignmentUid,
                                    ...((assignment.packageType ===
                                      PackageType.PdfUpload ||
                                      assignment.packageType ===
                                        PackageType.VideoUpload) && {
                                      packageType: assignment.packageType,
                                    }),
                                  });
                                  window.open(
                                    `/portal-configuration/${portalUid}/reports/assesment-report?${params}`,
                                    "_blank",
                                    "noopener,noreferrer",
                                  );
                                }}
                                sx={{
                                  fontSize: 14,
                                  fontWeight: 500,
                                  color: "#5354D8",
                                  p: 0,
                                  minWidth: 0,
                                  textTransform: "none",
                                  "&:hover": {
                                    textDecoration: "underline",
                                    background: "none",
                                  },
                                }}
                              >
                                Record {record.recordNo}
                              </Button>
                            </TableCell>
                            <TableCell>
                              <VersionChip
                                version={record.version}
                                isLatest={
                                  record.version != null &&
                                  record.version === latestRecord?.version
                                }
                              />
                            </TableCell>
                            <TableCell>
                              {record.portalGroupName || "Homepage"}
                            </TableCell>
                            <TableCell>
                              <StatusChip status={record.progressStatus} />
                            </TableCell>
                            <TableCell>
                              {assignment.packageType ===
                                PackageType.PdfUpload ||
                              assignment.packageType === PackageType.VideoUpload
                                ? "-"
                                : `${record.bestScore != null ? record.bestScore : "0"}% • ${record.attempts} ${record.attempts === 1 ? "Attempt" : "Attempts"}`}
                            </TableCell>
                            <TableCell>
                              {record.timeSpent
                                ? `${record.timeSpent} mins`
                                : "-"}
                            </TableCell>
                            <TableCell>
                              {formatDateCell(record.dueDate)}
                            </TableCell>
                            <TableCell>
                              {formatDateCell(record.startDate)}
                            </TableCell>
                            <TableCell>
                              {formatDateCell(record.completionDate)}
                            </TableCell>
                          </TableRow>
                        ))}
                  </React.Fragment>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 2 }}>
                  <Typography color="#475467" fontSize={12}>
                    No assignments found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {scrollbar.show && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: SCROLL_ARROW_W,
            backgroundColor: "#fff",
            userSelect: "none",
          }}
        >
          <Box
            sx={{
              width: SCROLL_ARROW_W,
              height: SCROLL_ARROW_W,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              flexShrink: 0,
            }}
            onMouseDown={() => scrollByAmount(-100)}
            onTouchStart={(e) => {
              e.preventDefault();
              scrollByAmount(-100);
            }}
          >
            <svg width="10" height="16" viewBox="0 0 6 10">
              <polygon points="5,1 1,5 5,9" fill="#344054" />
            </svg>
          </Box>
          <Box sx={{ flex: 1, position: "relative", height: SCROLL_ARROW_W }}>
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                transform: "translateY(-50%)",
                height: 16,
                width: scrollbar.thumbWidth,
                left: scrollbar.thumbLeft,
                backgroundColor: "#eaecf0",
                borderRadius: "10px",
                cursor: "grab",
                touchAction: "none",
                "&:active": { cursor: "grabbing" },
              }}
              ref={thumbRef}
              onMouseDown={(e) => {
                e.preventDefault();
                onThumbDragStart(e.clientX);
              }}
              onTouchStart={(e) => {
                e.preventDefault();
                onThumbDragStart(e.touches[0].clientX);
              }}
            />
          </Box>
          <Box
            sx={{
              width: SCROLL_ARROW_W,
              height: SCROLL_ARROW_W,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              flexShrink: 0,
            }}
            onMouseDown={() => scrollByAmount(100)}
            onTouchStart={(e) => {
              e.preventDefault();
              scrollByAmount(100);
            }}
          >
            <svg width="10" height="16" viewBox="0 0 6 10">
              <polygon points="1,1 5,5 1,9" fill="#344054" />
            </svg>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export const DetailedReportByPortalTable: React.FC<
  DetailedReportByPortalTableProps
> = ({ data }) => {
  const borderColor = "#EAECF0";
  const headerBg = "#F9FAFB";
  const { portalUid } = useParams<{ portalUid: string }>();
  const { showToast } = useToast();
  const { customisations } = useCustomisation();
  const {
    HOME: { assignmentStatusColorsSettings },
  } = customisations.customisations;

  const homeCompletedColor =
    assignmentStatusColorsSettings?.homeCompleted || "#5354D8";
  const homePendingColor =
    assignmentStatusColorsSettings?.homePending || "#F79009";
  const homeOverdueColor =
    assignmentStatusColorsSettings?.homeOverdue || "#FF6B6B";

  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${borderColor}`,
          borderRadius: "8px",
          overflow: "hidden",
          boxShadow: "0px 1px 3px 0px #1018281A",
          p: 3,
        }}
      >
        <Typography color="#475467" fontSize={14} align="center">
          No users found.
        </Typography>
      </Paper>
    );
  }

  return (
    <Stack direction="column" gap={3}>
      {data.map((portal, portalIndex) => (
        <Paper
          key={portal.portalUid || portalIndex}
          elevation={0}
          sx={{
            border: `1px solid ${borderColor}`,
            borderRadius: "8px",
            overflow: "hidden",
            boxShadow: "0px 1px 3px 0px #1018281A",
            p: 0,
          }}
        >
          {/* Portal header */}
          <Box
            sx={{
              backgroundColor: "#E0E3E8",
              px: 2,
              py: 1.5,
              borderBottom: `1px solid ${borderColor}`,
            }}
          >
            <Typography fontSize={14} fontWeight={600} color="#475467">
              Portal: {portal.name}
            </Typography>
          </Box>

          {portal.users && portal.users.length > 0 ? (
            portal.users.map((user, userIndex) => (
              <PortalUserSection
                key={user.userUid || userIndex}
                user={user}
                borderColor={borderColor}
                headerBg={headerBg}
                homeCompletedColor={homeCompletedColor}
                homePendingColor={homePendingColor}
                homeOverdueColor={homeOverdueColor}
                portalUid={portalUid}
                showToast={showToast}
                isLast={userIndex === portal.users.length - 1}
              />
            ))
          ) : (
            <Box sx={{ p: 2 }}>
              <Typography color="#475467" fontSize={12}>
                No users found
              </Typography>
            </Box>
          )}
        </Paper>
      ))}
    </Stack>
  );
};
